// WS client —— 单一连接，指数退避重连，inbound 事件分发，outbox 队列。
//
// 端口与适配器：
// - WsClient 不知道 React / DOM，只是个 EventEmitter-like wrapper
// - use-room.ts 把它适配成 React hook
// - Tauri/Web 都用同一份代码（浏览器原生 WebSocket）

export type WsStatus = "connecting" | "open" | "reconnecting" | "closed";

export interface WsClientOptions {
  url: string;
  room: string;
  device: string;
  /** E9.7: 当前认证用户 ID; relay 透传给 agent-host 给 quota 按 user 计数. 缺省时 fallback 到 device. */
  userId?: string;
  /** 重连时的 since=N（C3 协议）；首连传 undefined 即不回灌 */
  since?: number;
  /** 收到 message 的回调（已 trim）*/
  onMessage: (text: string) => void;
  /** 连接状态变化的回调 */
  onStatus?: (s: WsStatus) => void;
}

const INITIAL_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;
const JITTER = 0.2;

export class WsClient {
  private ws: WebSocket | null = null;
  private status: WsStatus = "closed";
  private closed = false;
  private retry = 0;
  private outbox: string[] = [];

  constructor(private readonly opts: WsClientOptions) {}

  connect(): void {
    if (this.closed) return;
    this.setStatus(this.retry === 0 ? "connecting" : "reconnecting");

    const url = this.buildUrl();
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.retry = 0;
      this.setStatus("open");
      this.flushOutbox();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") this.opts.onMessage(ev.data);
    };
    ws.onerror = () => {
      // onerror 后浏览器一定会触发 onclose，统一在 onclose 里处理重连
    };
    ws.onclose = () => {
      this.ws = null;
      if (this.closed) {
        this.setStatus("closed");
      } else {
        this.scheduleReconnect();
      }
    };
  }

  send(text: string): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(text);
    } else {
      this.outbox.push(text);
    }
  }

  close(): void {
    this.closed = true;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.setStatus("closed");
  }

  getStatus(): WsStatus {
    return this.status;
  }

  // ---- internal ----

  private buildUrl(): string {
    const params = new URLSearchParams({
      room: this.opts.room,
      device: this.opts.device,
    });
    if (this.opts.userId) {
      params.set("user_id", this.opts.userId);
    }
    if (this.opts.since !== undefined && this.opts.since > 0) {
      params.set("since", String(this.opts.since));
    }
    return `${this.opts.url}?${params.toString()}`;
  }

  private flushOutbox(): void {
    while (this.outbox.length > 0 && this.ws?.readyState === WebSocket.OPEN) {
      const msg = this.outbox.shift()!;
      this.ws.send(msg);
    }
  }

  private scheduleReconnect(): void {
    this.setStatus("reconnecting");
    const base = Math.min(
      INITIAL_BACKOFF_MS * 2 ** this.retry,
      MAX_BACKOFF_MS,
    );
    const jitter = base * JITTER * (Math.random() * 2 - 1);
    const delay = Math.max(0, Math.floor(base + jitter));
    this.retry += 1;
    setTimeout(() => {
      if (!this.closed) this.connect();
    }, delay);
  }

  private setStatus(s: WsStatus): void {
    if (this.status !== s) {
      this.status = s;
      this.opts.onStatus?.(s);
    }
  }
}
